import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Package, Search, Eye, Loader2, Filter, X, Box, Tag
} from 'lucide-react';
import api from '../../utils/api';

const MobileInventory = () => {
  const { user } = useAuth();
  const [inventory, setInventory] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Filter states
  const [filterBrand, setFilterBrand] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterModel, setFilterModel] = useState('all');

  const fetchInventory = useCallback(async () => {
    try {
      const response = await api.get('/inventory/');
      // Filter out 0 stock items - only show available stock
      const availableStock = response.data.filter(item => (item.qty_in_stock || 0) > 0);
      setInventory(availableStock);
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMasterData = useCallback(async () => {
    try {
      const [brandsRes, categoriesRes] = await Promise.all([
        api.get('/brands/'),
        api.get('/categories/')
      ]);
      setBrands(brandsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Failed to fetch master data');
    }
  }, []);

  useEffect(() => {
    fetchInventory();
    fetchMasterData();
  }, [fetchInventory, fetchMasterData]);

  // Get unique models from inventory
  const uniqueModels = [...new Set(inventory.map(item => item.product_model).filter(Boolean))];

  // Filter inventory
  const filteredInventory = inventory.filter(item => {
    // Search filter
    const matchesSearch = !searchTerm || 
      (item.product_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.product_sku || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.serial_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.product_model || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    // Brand filter
    const matchesBrand = filterBrand === 'all' || item.brand_id === filterBrand;
    
    // Category filter
    const matchesCategory = filterCategory === 'all' || item.category_id === filterCategory;
    
    // Model filter
    const matchesModel = filterModel === 'all' || item.product_model === filterModel;
    
    return matchesSearch && matchesBrand && matchesCategory && matchesModel;
  });

  // Group by product for summary view
  const productSummary = filteredInventory.reduce((acc, item) => {
    const productId = item.product_id;
    if (!acc[productId]) {
      acc[productId] = {
        product_id: productId,
        product_name: item.product_name,
        product_sku: item.product_sku,
        product_model: item.product_model,
        brand_name: item.brand_name,
        brand_id: item.brand_id,
        category_name: item.category_name,
        category_id: item.category_id,
        selling_price: item.selling_price,
        purchase_price: item.purchase_price,
        reorder_level: item.reorder_level || 5,
        totalQty: 0,
        totalValue: 0,
        items: []
      };
    }
    acc[productId].totalQty += item.qty_in_stock || 0;
    acc[productId].totalValue += item.stock_value || 0;
    acc[productId].items.push(item);
    return acc;
  }, {});

  const productList = Object.values(productSummary);

  // Calculate totals
  const totalQty = filteredInventory.reduce((sum, item) => sum + (item.qty_in_stock || 0), 0);
  const totalValue = filteredInventory.reduce((sum, item) => sum + (item.stock_value || 0), 0);

  const hasActiveFilters = filterBrand !== 'all' || filterCategory !== 'all' || filterModel !== 'all';

  const clearFilters = () => {
    setFilterBrand('all');
    setFilterCategory('all');
    setFilterModel('all');
    setSearchTerm('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="p-4 pb-24 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-bold text-gray-800">Available Stock</h1>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setFilterDialogOpen(true)}
            className={hasActiveFilters ? 'border-emerald-500 text-emerald-600' : ''}
          >
            <Filter className="w-4 h-4 mr-1" />
            Filter
            {hasActiveFilters && <Badge className="ml-1 bg-emerald-500 text-white text-xs">!</Badge>}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search product, SKU, model..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-white border-gray-200"
        />
        {searchTerm && (
          <button 
            onClick={() => setSearchTerm('')}
            className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs text-gray-500">Filters:</span>
          {filterBrand !== 'all' && (
            <Badge variant="outline" className="bg-blue-50 text-blue-700 flex items-center gap-1">
              Brand: {brands.find(b => b.id === filterBrand)?.name || filterBrand}
              <X className="w-3 h-3 cursor-pointer" onClick={() => setFilterBrand('all')} />
            </Badge>
          )}
          {filterCategory !== 'all' && (
            <Badge variant="outline" className="bg-purple-50 text-purple-700 flex items-center gap-1">
              Category: {categories.find(c => c.id === filterCategory)?.name || filterCategory}
              <X className="w-3 h-3 cursor-pointer" onClick={() => setFilterCategory('all')} />
            </Badge>
          )}
          {filterModel !== 'all' && (
            <Badge variant="outline" className="bg-orange-50 text-orange-700 flex items-center gap-1">
              Model: {filterModel}
              <X className="w-3 h-3 cursor-pointer" onClick={() => setFilterModel('all')} />
            </Badge>
          )}
          <Button variant="link" size="sm" className="text-red-500 p-0 h-auto" onClick={clearFilters}>
            Clear All
          </Button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-2">
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600">
          <CardContent className="p-3 text-center text-white">
            <p className="text-2xl font-bold">{productList.length}</p>
            <p className="text-xs opacity-90">Products</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600">
          <CardContent className="p-3 text-center text-white">
            <p className="text-2xl font-bold">{totalQty.toLocaleString('en-IN')}</p>
            <p className="text-xs opacity-90">Total Qty</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600">
          <CardContent className="p-3 text-center text-white">
            <p className="text-lg font-bold">₹{(totalValue / 1000).toFixed(1)}K</p>
            <p className="text-xs opacity-90">Stock Value</p>
          </CardContent>
        </Card>
      </div>

      {/* Inventory List */}
      <div className="space-y-3">
        {productList.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="p-8 text-center text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No available stock items found</p>
              {hasActiveFilters && (
                <Button variant="link" onClick={clearFilters} className="mt-2">
                  Clear filters to see all items
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          productList.map((group) => {
            const isLowStock = group.totalQty <= group.reorder_level;
            return (
              <Card 
                key={group.product_id} 
                className={`bg-white border-l-4 ${isLowStock ? 'border-l-orange-500' : 'border-l-emerald-500'}`}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">{group.product_name || 'Unknown Product'}</p>
                      <p className="text-sm text-gray-500">SKU: {group.product_sku || 'N/A'}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {group.product_model && (
                          <Badge variant="outline" className="text-xs bg-gray-50">
                            {group.product_model}
                          </Badge>
                        )}
                        {group.brand_name && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                            {group.brand_name}
                          </Badge>
                        )}
                        {group.category_name && (
                          <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">
                            {group.category_name}
                          </Badge>
                        )}
                      </div>
                    </div>
                    {isLowStock && (
                      <Badge className="bg-orange-100 text-orange-700">Low Stock</Badge>
                    )}
                  </div>
                  
                  <div className="flex justify-between items-center mt-3">
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className={`text-2xl font-bold ${isLowStock ? 'text-orange-600' : 'text-emerald-600'}`}>
                          {group.totalQty}
                        </p>
                        <p className="text-xs text-gray-500">Available</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-semibold text-gray-600">
                          ₹{group.selling_price?.toLocaleString('en-IN') || '0'}
                        </p>
                        <p className="text-xs text-gray-500">Price</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-green-600">
                          ₹{group.totalValue?.toLocaleString('en-IN') || '0'}
                        </p>
                        <p className="text-xs text-gray-500">Value</p>
                      </div>
                    </div>
                    
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                      onClick={() => { setSelectedItem(group); setViewDialogOpen(true); }}
                    >
                      <Eye className="w-4 h-4 mr-1" /> Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Filter Dialog */}
      <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-emerald-600" />
              Filter Stock
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Brand Filter */}
            <div className="space-y-2">
              <Label className="text-sm text-gray-600">Brand</Label>
              <Select value={filterBrand} onValueChange={setFilterBrand}>
                <SelectTrigger>
                  <SelectValue placeholder="All Brands" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Brands</SelectItem>
                  {brands.map(brand => (
                    <SelectItem key={brand.id} value={brand.id}>
                      {brand.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Category Filter */}
            <div className="space-y-2">
              <Label className="text-sm text-gray-600">Category</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(category => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Model Filter */}
            <div className="space-y-2">
              <Label className="text-sm text-gray-600">Model</Label>
              <Select value={filterModel} onValueChange={setFilterModel}>
                <SelectTrigger>
                  <SelectValue placeholder="All Models" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Models</SelectItem>
                  {uniqueModels.map(model => (
                    <SelectItem key={model} value={model}>
                      {model}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" className="flex-1" onClick={clearFilters}>
                Clear All
              </Button>
              <Button className="flex-1 bg-emerald-600" onClick={() => setFilterDialogOpen(false)}>
                Apply Filters
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-emerald-700">{selectedItem?.product_name}</DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-gray-500">SKU</p>
                  <p className="font-medium">{selectedItem.product_sku || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-gray-500">Model</p>
                  <p className="font-medium">{selectedItem.product_model || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-gray-500">Brand</p>
                  <p className="font-medium">{selectedItem.brand_name || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-gray-500">Category</p>
                  <p className="font-medium">{selectedItem.category_name || 'N/A'}</p>
                </div>
                <div className="bg-emerald-50 p-3 rounded-lg">
                  <p className="text-emerald-600">Available Qty</p>
                  <p className="font-bold text-emerald-700 text-xl">{selectedItem.totalQty}</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-blue-600">Stock Value</p>
                  <p className="font-bold text-blue-700">₹{selectedItem.totalValue?.toLocaleString('en-IN')}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-gray-500">Selling Price</p>
                  <p className="font-medium">₹{selectedItem.selling_price?.toLocaleString('en-IN') || '0'}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-gray-500">Purchase Price</p>
                  <p className="font-medium">₹{selectedItem.purchase_price?.toLocaleString('en-IN') || '0'}</p>
                </div>
              </div>

              {/* Individual Items */}
              {selectedItem.items && selectedItem.items.length > 1 && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Stock Breakdown ({selectedItem.items.length} entries)</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedItem.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm">
                        <div>
                          <p className="font-medium">{item.serial_number || `Entry ${idx + 1}`}</p>
                          {item.supplier_name && (
                            <p className="text-xs text-gray-500">Supplier: {item.supplier_name}</p>
                          )}
                        </div>
                        <Badge variant="outline">{item.qty_in_stock} qty</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileInventory;
